import { supabase } from '../lib/supabase'

export interface BugReport {
  id?: string
  userId: string
  userEmail: string
  userName: string
  title: string
  description: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  category: 'general' | 'ui' | 'performance' | 'data' | 'auth' | 'billing'
  steps?: string
  expectedBehavior?: string
  actualBehavior?: string
  browserInfo: string
  url: string
  status?: 'open' | 'in_progress' | 'resolved' | 'closed'
  createdAt?: string
  updatedAt?: string
}

class BugReportService {
  async submitBugReport(bugReport: Omit<BugReport, 'id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<BugReport> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const { data, error } = await supabase
        .from('bug_reports')
        .insert([{
          user_id: bugReport.userId,
          user_email: bugReport.userEmail,
          user_name: bugReport.userName,
          title: bugReport.title,
          description: bugReport.description,
          severity: bugReport.severity,
          category: bugReport.category,
          steps: bugReport.steps || null,
          expected_behavior: bugReport.expectedBehavior || null,
          actual_behavior: bugReport.actualBehavior || null,
          browser_info: bugReport.browserInfo,
          url: bugReport.url,
          status: 'open'
        }])
        .select()
        .single()

      if (error) {
        console.error('Error submitting bug report:', error)
        throw new Error('Failed to submit bug report')
      }

      return {
        id: data.id,
        userId: data.user_id,
        userEmail: data.user_email,
        userName: data.user_name,
        title: data.title,
        description: data.description,
        severity: data.severity,
        category: data.category,
        steps: data.steps,
        expectedBehavior: data.expected_behavior,
        actualBehavior: data.actual_behavior,
        browserInfo: data.browser_info,
        url: data.url,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      }
    } catch (error) {
      console.error('Error in submitBugReport:', error)
      throw error
    }
  }

  async getBugReports(userId?: string): Promise<BugReport[]> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      // If userId is provided, get reports for that specific user
      // If no userId, this will only work for admin users due to RLS policies
      let query = supabase
        .from('bug_reports')
        .select('*')
        .order('created_at', { ascending: false })

      if (userId) {
        query = query.eq('user_id', userId)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching bug reports:', error)
        throw new Error('Failed to fetch bug reports')
      }

      return data.map(report => ({
        id: report.id,
        userId: report.user_id,
        userEmail: report.user_email,
        userName: report.user_name,
        title: report.title,
        description: report.description,
        severity: report.severity,
        category: report.category,
        steps: report.steps,
        expectedBehavior: report.expected_behavior,
        actualBehavior: report.actual_behavior,
        browserInfo: report.browser_info,
        url: report.url,
        status: report.status,
        createdAt: report.created_at,
        updatedAt: report.updated_at
      }))
    } catch (error) {
      console.error('Error in getBugReports:', error)
      throw error
    }
  }

  async updateBugReportStatus(id: string, status: BugReport['status']): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const { error } = await supabase
        .from('bug_reports')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) {
        console.error('Error updating bug report status:', error)
        throw new Error('Failed to update bug report status')
      }
    } catch (error) {
      console.error('Error in updateBugReportStatus:', error)
      throw error
    }
  }

  async deleteBugReport(id: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const { error } = await supabase
        .from('bug_reports')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('Error deleting bug report:', error)
        throw new Error('Failed to delete bug report')
      }
    } catch (error) {
      console.error('Error in deleteBugReport:', error)
      throw error
    }
  }
}

export const bugReportService = new BugReportService() 